import {
  Button,
  RFQCard,
  FiltersBar,
  filtersConfig,
} from "@b2b/ui-components";

import { Plus, XCircle, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { rfqService } from "@b2b/services";
import { Rfq } from "@b2b/services/api/modules/rfq/rfq.types";

interface ToastState {
  type: "success" | "error";
  message: string;
}

export default function RFQDashboard() {
  const [rfqs, setRfqs] = useState<Rfq[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    closed: 0,
    draft: 0,
  });

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    fetchRfqs();
  }, [currentPage]);

  const fetchRfqs = async () => {
    try {
      setLoading(true);
      const response = await rfqService.getRfqList({ 
        page: currentPage, 
        limit: 20,
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      setRfqs(response.items || []);
      setTotalPages(response.totalPages || 1);
      const totalCount = response.total || 0;
      setTotal(totalCount);
      
      // Calculate stats by fetching each status separately (with max limit of 100)
      // This is more efficient than trying to fetch all at once
      try {
        const [openResponse, closedResponse, draftResponse] = await Promise.all([
          rfqService.getRfqList({ page: 1, limit: 100, status: "OPEN" as any }),
          rfqService.getRfqList({ page: 1, limit: 100, status: "CLOSED" as any }),
          rfqService.getRfqList({ page: 1, limit: 100, status: "DRAFT" as any }),
        ]);

        setStats({
          total: totalCount,
          open: openResponse.total || openResponse.items?.length || 0,
          closed: closedResponse.total || closedResponse.items?.length || 0,
          draft: draftResponse.total || draftResponse.items?.length || 0,
        });
      } catch (statsError) {
        // If stats fetch fails, use total from main response
        console.warn("Error fetching stats:", statsError);
        setStats({
          total: totalCount,
          open: 0,
          closed: 0,
          draft: 0,
        });
      }
    } catch (error: any) {
      console.error("Error fetching RFQs:", error);
      showToast("error", error?.response?.data?.message || "Failed to load RFQs");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="w-full h-full bg-background">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 p-4 rounded-lg shadow-lg max-w-[400px] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <p className="flex-1 font-medium text-sm">{toast.message}</p>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <main className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex flex-col gap-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">
              RFQ Dashboard
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Find the best suppliers and manage your procurement needs
            </p>
          </div>
          <Button asChild variant="primary" className="w-full sm:w-auto">
            <Link to="/buyer/rfq_create_form" className="flex items-center justify-center">
              <Plus className="h-4 w-4 mr-2" />
              Post New RFQ
            </Link>
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card p-4 rounded-lg border shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Total RFQs</p>
                <p className="text-xl sm:text-2xl font-bold text-card-foreground">
                  {stats.total}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-card p-4 rounded-lg border shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Open RFQs</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">
                  {stats.open}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-card p-4 rounded-lg border shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Closed RFQs</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-600">
                  {stats.closed}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-card p-4 rounded-lg border shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Draft RFQs</p>
                <p className="text-xl sm:text-2xl font-bold text-yellow-600">
                  {stats.draft}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <FiltersBar filters={filtersConfig} showSearch />
        </div>

        {/* RFQ Grid */}
        {loading ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">Loading RFQs...</p>
          </div>
        ) : rfqs.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">
              No RFQs available. Create your first RFQ to get started.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {rfqs.map((rfq) => {
              // Map API response to RFQCard expected format
              const mappedRfq = {
                id: rfq.id,
                title: rfq.title,
                company: rfq.buyer?.company?.name || "N/A",
                location: rfq.buyer?.company?.name || "N/A",
                postedDate: new Date(rfq.createdAt).toLocaleDateString(),
                expiryDate: rfq.expiryDate ? new Date(rfq.expiryDate).toLocaleDateString() : "N/A",
                quantity: `${rfq.quantity} ${rfq.unit}`,
                quotesCount: rfq.quoteCount || (rfq as any).quotes?.length || 0,
                description: rfq.description || "",
                category: rfq.categories?.[0]?.name || rfq.categoryIds?.[0] || "N/A",
                status: rfq.status === "OPEN" ? "active" : rfq.status === "CLOSED" ? "closed" : rfq.status === "CANCELLED" ? "expired" : "active",
              };
              return <RFQCard key={rfq.id} rfq={mappedRfq} />;
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6 sm:mt-8">
            <div className="flex items-center space-x-2 flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant="outline"
                    size="sm"
                    className={currentPage === pageNum ? "bg-primary text-primary-foreground" : ""}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
